import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export const ADMIN_INSTANCE_STORAGE_KEY = 'kuutio-admin-instance';

function readInstanceFromParams(searchParams) {
  return searchParams.get('instance')
    || searchParams.get('kid')
    || searchParams.get('instanceId');
}

/** The instance last worked on in admin (or as participant), from localStorage. */
export function readRememberedInstanceId() {
  try {
    return localStorage.getItem(ADMIN_INSTANCE_STORAGE_KEY)
      || localStorage.getItem('kuutio-participant-instance');
  } catch {
    return null;
  }
}

/**
 * Resolves the active admin instance from URL query params, with localStorage
 * fallback so navigation between admin tabs keeps the same instance selected.
 */
export function useAdminInstance({ restoreFromStorage = true } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const instanceId = readInstanceFromParams(searchParams);

  useEffect(() => {
    if (instanceId) {
      localStorage.setItem(ADMIN_INSTANCE_STORAGE_KEY, instanceId);
      return;
    }
    if (!restoreFromStorage) return;

    const saved = readRememberedInstanceId();
    if (saved) {
      setSearchParams({ instance: saved }, { replace: true });
    }
  }, [instanceId, restoreFromStorage, setSearchParams]);

  const selectInstance = useCallback((id) => {
    localStorage.setItem(ADMIN_INSTANCE_STORAGE_KEY, id);
    setSearchParams({ instance: id });
  }, [setSearchParams]);

  const buildInstancePath = useCallback((path) => {
    if (!instanceId) return path;
    const separator = path.includes('?') ? '&' : '?';
    return `${path}${separator}instance=${instanceId}`;
  }, [instanceId]);

  return { instanceId, selectInstance, buildInstancePath };
}

export default useAdminInstance;
